import Modal from "@/components/modal"
import RiskListItemDetail from "../RiskListItemDetail"
import UnitBadge from "../UnitBadge"
import { Risk } from "@/features/risks/entities/risk/model/risk.types"
import {
  faChevronLeft,
  faChevronRight,
  faLayerGroup,
  faTimes,
  faPen,
  faClipboardList
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Tooltip from "@/components/tooltip"
import ListButton from "@/components/list-button"
import Button from "@/components/button"
import { useTranslation } from "react-i18next"
import { useEffect } from "react"
import getColor from "@/utils/get-color.utils"

interface Props {
  risk: Risk | null
  allRisks: Risk[]
  onClose: () => void
  onNavigateNext: () => void
  onNavigatePrevious: () => void
  onEdit?: () => void
  isIncomplete?: boolean
  isDateFiltered?: boolean | null
}

export default function RiskDetailModal({
  risk,
  allRisks,
  onClose,
  onNavigateNext,
  onNavigatePrevious,
  onEdit,
  isIncomplete,
  isDateFiltered
}: Props) {
  const { t } = useTranslation()

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault()
        onNavigatePrevious()
      } else if (e.key === "ArrowRight") {
        e.preventDefault()
        onNavigateNext()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [onNavigateNext, onNavigatePrevious])

  if (!risk) return null

  return (
    <Modal
      onClose={onClose}
      isLarge
      footer={
        <div className="flex items-center w-full gap-2">
          <div className="w-[140px]">
            {isIncomplete && (
              <div className="flex items-center gap-2">
                <div
                  className="flex items-center justify-center rounded-[50%] aspect-square flex-shrink-0"
                  style={{ backgroundColor: getColor("red"), height: "24px" }}>
                  <FontAwesomeIcon
                    icon={faClipboardList}
                    style={{
                      fontSize: "12px",
                      color: "white"
                    }}
                  />
                </div>
                <div
                  className="text-xs px-2 py-1 rounded text-white w-fit"
                  style={{ backgroundColor: getColor("red") }}>
                  {t("À compléter")}
                </div>
              </div>
            )}
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <Tooltip
              text={
                allRisks[(allRisks.findIndex((r) => r.id === risk?.id) - 1 + allRisks.length) % allRisks.length]
                  ?.attributes
                  ? Object.values(
                      allRisks[(allRisks.findIndex((r) => r.id === risk?.id) - 1 + allRisks.length) % allRisks.length]
                        .attributes
                    )[1]
                  : ""
              }
              position="top">
              <ListButton onClick={onNavigatePrevious} icon={faChevronLeft} />
            </Tooltip>
            <Tooltip
              text={
                allRisks[(allRisks.findIndex((r) => r.id === risk?.id) + 1) % allRisks.length]?.attributes
                  ? Object.values(
                      allRisks[(allRisks.findIndex((r) => r.id === risk?.id) + 1) % allRisks.length].attributes
                    )[1]
                  : ""
              }
              position="top">
              <ListButton onClick={onNavigateNext} icon={faChevronRight} />
            </Tooltip>
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <Button className="med" onClick={onClose} icon={faTimes} isWithBorder>
              {t("utils_close")}
            </Button>
            {onEdit && (
              <Tooltip text={isDateFiltered ? "Édition désactivée (consultation historique)" : ""} position="top">
                <div style={{ opacity: isDateFiltered ? 0.4 : 1, pointerEvents: isDateFiltered ? "none" : "auto" }}>
                  <Button
                    className="primary"
                    onClick={() => {
                      onClose()
                      onEdit()
                    }}
                    icon={faPen}>
                    {t("utils_update")}
                  </Button>
                </div>
              </Tooltip>
            )}
          </div>
        </div>
      }>
      <div>
        <div className="flex items-center gap-3 mb-4">
          <FontAwesomeIcon icon={faLayerGroup} className="grey-t flex-shrink-0" />
          <h2 className="text-2xl font-bold my-0 overflow-hidden text-ellipsis whitespace-nowrap max-w-3xl">
            {risk.name}
          </h2>
        </div>
        {(risk.unitName || (risk.associatedRisks && risk.associatedRisks.length > 0)) && (
          <div className="pt-0 pb-2 border-b border-gray-200 mb-4">
            <h4 className="text-xs font-semibold text-gray-500 mb-2">Unités concernées</h4>
            <div className="flex flex-wrap gap-2">
              {risk.unitName && (
                <UnitBadge unitName={risk.unitName} unitSize={risk.unitSize} isCurrentUnit={true} isClickable={false} />
              )}
              {risk.associatedRisks?.map(
                (associatedRisk) =>
                  associatedRisk.unitName && (
                    <UnitBadge
                      key={associatedRisk.id}
                      unitName={associatedRisk.unitName}
                      unitSize={associatedRisk.unitSize}
                      isCurrentUnit={false}
                      isClickable={false}
                    />
                  )
              )}
            </div>
          </div>
        )}
        <RiskListItemDetail risk={risk} showCheckbox={false} hideFirstAttribute={true} containerClassName="px-0" />
      </div>
    </Modal>
  )
}
